import React, { useState } from "react";
import "./PremiumHeader.css";
import { Button, Tooltip, message } from "antd";
import {
  LeftOutlined, RightOutlined, SaveOutlined,
  ReloadOutlined, DownloadOutlined, FileDoneOutlined,
} from "@ant-design/icons";
import { useDocumentStore } from "../../../store/vendorRegistrationStore/useDocumentStore";
import { useIrnStore } from "../../../store/useIrnstore";
import { useTradeStore } from "../../../store/vendorRegistrationStore/useTradeStore";
import { useThemeStore } from "../../../store/useThemeStore";

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

const STEP_ICONS = {
  "General Info":       "👤",
  "Basic Data":         "📋",
  "Commercial Terms":   "💼",
  "Trade Support Terms":"🤝",
  "Document":           "📄",
};

const DOC_LABELS = {
  panFile:              "PAN Card",
  gstFile:              "GST Document",
  cancelledCheque:      "Cancelled Cheque",
  fssaiCertificate:     "FSSAI Certificate",
  msmeDocument:         "MSME Document",
  rtgsDetails:          "RTGS Certificate",
  businessCodeOfConduct:"Business COC",
  articalOfAssociation: "Article of Association",
  partnershipDeed:      "Partnership Deed",
  organicCategory:      "Organic Category",
  diabeticCategory:     "Diabetic Category",
  vattincstNumber:      "VAT / TIN / CST",
};

const PremiumHeader = ({
  currentStep, stepTitle, totalSteps, steps,
  onPrevious, onNext, onReset, onSubmit, showStep4,
}) => {
  const { themeColor } = useThemeStore();
  const rgb = hexToRgb(themeColor);

  const submitted = localStorage.getItem("submitted") === "Yes";
  const [dlLoading, setDlLoading] = useState(false);

  const { documnetData } = useDocumentStore();
  const { tradeData }    = useTradeStore();
  const { downloadDoc }  = useIrnStore();

  const { locationCode, ...restDocs } = documnetData.documents[0];

  const handleDownload = async (name) => {
    try {
      setDlLoading(true);
      const response = await downloadDoc(name);
      const link = document.createElement("a");
      link.href = response; link.download = name;
      document.body.appendChild(link); link.click(); link.remove();
      message.success("Downloaded successfully.");
    } catch {
      message.error("Failed to download.");
    } finally { setDlLoading(false); }
  };

  return (
    <>
      {/* ── Main header bar ─────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 20px", gap: 16, flexWrap: "wrap",
        background: "#fff", borderRadius: 12,
        border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
      }}>

        {/* Left — icon + step title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: themeColor, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 3px 10px ${themeColor}44`, transition: "background 0.3s",
          }}>
            <FileDoneOutlined style={{ fontSize: 16, color: "#fff" }} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827" }}>{stepTitle}</p>
            <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>Step {currentStep} of {totalSteps}</p>
          </div>
        </div>

        {/* Center — step progress */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, justifyContent: "center", minWidth: 0, flexWrap: "nowrap" }}>
          <Button
            icon={<LeftOutlined style={{ fontSize: 11 }} />}
            onClick={onPrevious} disabled={currentStep === 1}
            size="small" type="text"
            style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }}
          />

          {steps.map((step, idx) => {
            const num = idx + 1;
            const isActive    = num === currentStep;
            const isCompleted = num < currentStep;

            return (
              <React.Fragment key={num}>
                <Tooltip title={step} placement="bottom">
                  <div style={{
                    width: isActive ? 30 : 26, height: isActive ? 30 : 26,
                    borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.3s",
                    background: isCompleted
                      ? "#16a34a"
                      : isActive
                        ? themeColor
                        : "#f4f5f7",
                    border: `2px solid ${isCompleted ? "#16a34a" : isActive ? themeColor : "#e5e7eb"}`,
                    boxShadow: isActive ? `0 3px 10px ${themeColor}44` : isCompleted ? "0 2px 6px rgba(22,163,74,0.25)" : "none",
                    transform: isActive ? "scale(1.1)" : "scale(1)",
                    cursor: "default",
                  }}>
                    {isCompleted ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17L4 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, color: isActive ? "#fff" : "#9ca3af" }}>{num}</span>
                    )}
                  </div>
                </Tooltip>

                {idx < steps.length - 1 && (
                  <div style={{
                    width: 28, height: 2, borderRadius: 1, flexShrink: 0,
                    background: isCompleted ? "#16a34a" : "#e5e7eb",
                    transition: "background 0.4s",
                  }} />
                )}
              </React.Fragment>
            );
          })}

          <Button
            icon={<RightOutlined style={{ fontSize: 11 }} />}
            onClick={onNext} disabled={currentStep === totalSteps}
            size="small" type="text"
            style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }}
          />
        </div>

        {/* Right — action buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
          <Tooltip title="Reset to Step 1">
            <Button
              icon={<ReloadOutlined />} onClick={onReset}
              disabled={submitted} danger size="middle"
              style={{ height: 34, borderRadius: 8 }}
            />
          </Tooltip>
          <Button
            icon={<SaveOutlined />} onClick={onSubmit}
            disabled={submitted || currentStep !== totalSteps}
            style={{
              height: 34, borderRadius: 8, fontWeight: 700, fontSize: 13,
              background: (!submitted && currentStep === totalSteps) ? themeColor : undefined,
              borderColor: (!submitted && currentStep === totalSteps) ? themeColor : undefined,
              color: (!submitted && currentStep === totalSteps) ? "#fff" : undefined,
              boxShadow: (!submitted && currentStep === totalSteps) ? `0 2px 8px ${themeColor}44` : undefined,
              transition: "all 0.3s",
            }}
          >
            Submit
          </Button>
        </div>
      </div>

      {/* ── Document download bar (shown when showStep4) ─────────────────── */}
      {showStep4 && (
        <div style={{
          marginTop: 8, padding: "10px 16px",
          background: "#fff", borderRadius: 10,
          border: "1px solid #f0f1f3", boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8,
        }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0 }}>
            Documents
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(restDocs).map(([key, value]) => {
              if (!value) return null;
              return (
                <Tooltip key={key} title={`Download ${DOC_LABELS[key] || key}`} placement="bottom">
                  <button
                    onClick={() => handleDownload(value)}
                    disabled={dlLoading}
                    style={{
                      display: "flex", alignItems: "center", gap: 5,
                      padding: "3px 10px", borderRadius: 20,
                      border: `1px solid rgba(${rgb},0.25)`,
                      background: `rgba(${rgb},0.07)`,
                      color: themeColor, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `rgba(${rgb},0.14)`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `rgba(${rgb},0.07)`; }}
                  >
                    <DownloadOutlined style={{ fontSize: 11 }} />
                    {DOC_LABELS[key] || key}
                  </button>
                </Tooltip>
              );
            })}

            {tradeData?.totAgreement && (
              <Tooltip title="Download TOT Agreement" placement="bottom">
                <button
                  onClick={() => handleDownload(tradeData.totAgreement)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, border: `1px solid rgba(${rgb},0.25)`, background: `rgba(${rgb},0.07)`, color: themeColor, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                >
                  <DownloadOutlined style={{ fontSize: 11 }} /> TOT Agreement
                </button>
              </Tooltip>
            )}
            {tradeData?.priceAndMargin && (
              <Tooltip title="Download Price & Margin" placement="bottom">
                <button
                  onClick={() => handleDownload(tradeData.priceAndMargin)}
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, border: `1px solid rgba(${rgb},0.25)`, background: `rgba(${rgb},0.07)`, color: themeColor, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                >
                  <DownloadOutlined style={{ fontSize: 11 }} /> Price & Margin
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PremiumHeader;
